import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChild,
  inject} from '@angular/core';

import { SelectionAction, ExportSelection } from '../types';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs';
import { AbstractSelectionService } from '../services/selections/abstract-selection-service';

@Component({
  selector: 'lib-selection-bar',
  templateUrl: './selection-bar.component.html',
  styleUrls: ['./selection-bar.component.scss'],
  standalone: false
})
export class SelectionBarComponent<T> implements OnInit, OnDestroy, AfterViewInit {

  protected selectionService = inject(AbstractSelectionService<T>);

  @ViewChild('selectionBar')
  selectionBar: ElementRef;

  @ViewChild('actionsDropdown')
  actionsDropdown: any;

  @Output()
  barHeight = new EventEmitter<number>();

  @Input()
  filteredItems: T[];

  @Input()
  allItems: T[];

  @Input()
  selectionActions: SelectionAction<T>[];

  @Input()
  selectionMarkingActions: SelectionAction<T>[];

  @Input()
  actionButtonTitle: string;

  @Output()
  selectedItemsToggle = new EventEmitter<string[]>();

  selectedToggle = false;

  unsubscribe = new Subject<void>();

  selection: ExportSelection<T> = {
    selectedItems: {},
    selectedItemCount: 0
  };

  actions: SelectionAction<T>[] = [];
  actionGroups: { groupName: string; actions: SelectionAction<T>[]; isOpen?: boolean }[] = [];

  performActionAndClose(action: SelectionAction<T>) {
    action.performAction(this.selection);
    if (this.actionsDropdown) {
      this.actionsDropdown.close();
    }
  }

  onDropdownOpenChange(isOpen: boolean) {
    if (!isOpen) {
      // Close all groups when dropdown closes
      for (const group of this.actionGroups) {
        group.isOpen = false;
      }
    }
  }

  private updateActionsAndGroups() {
    this.actions = this.selectionActions?.filter(action => !action.groupName) || [];

    if (!this.selectionActions) {
      this.actionGroups = [];
      return;
    }

    const groups = new Map<string, SelectionAction<T>[]>();
    this.selectionActions.forEach(action => {
      if (action.groupName) {
        if (!groups.has(action.groupName)) {
          groups.set(action.groupName, []);
        }
        groups.get(action.groupName).push(action);
      }
    });

    this.actionGroups = Array.from(groups.entries()).map(([groupName, actions]) => ({
      groupName,
      actions
    }));
  }

  ngOnInit() {
    this.selectionService
      .getSelection()
      .pipe(takeUntil(this.unsubscribe.asObservable()))
      .subscribe(selection => {
        this.selection = selection;
      });

    this.selectionService.triggerShowSelectedItems.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.selectedToggle = false;
      this.toggleSelectedItems();
    });

    this.updateActionsAndGroups();
  }

  ngAfterViewInit() {
    this.barHeight.emit((this.selectionBar.nativeElement as HTMLElement).offsetHeight);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getSelectionMarkingActions(): SelectionAction<T>[] {
    return this.selectionMarkingActions;
  }

  selectAllFilteredItems() {
    this.selectionService.select(this.filteredItems);
  }

  selectAllItems() {
    this.selectionService.select(this.allItems);
  }

  toggleSelectedItems(forceState?: boolean) {
    if (this.selection.selectedItemCount !== 0) {
      if (!this.selectedToggle || forceState) {
        this.selectedItemsToggle.emit(Object.keys(this.selection.selectedItems));
        if (!forceState || !this.selectedToggle) {
          this.selectedToggle = !this.selectedToggle;
        }
      } else {
        this.selectedItemsToggle.emit([]);
        this.selectedToggle = !this.selectedToggle;
      }
    }
    this.selectionService.showSelectedItems = this.selectedToggle;
  }

  clearSelection() {
    this.selectionService.clearSelection();
  }

  deselectAllFilteredItems() {
    this.selectionService.deselect(this.filteredItems);
  }
}
